import { useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import styles from './ArticleText.module.scss';
import { ConsentPlaceholder, Container } from 'ui/components';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { useYouTubeIframeConsent } from 'application/hooks/useYouTubeIframeConsent';
import { deferYouTubeIframes } from 'helpers/youtubeEmbed';

export interface ArticleTextProps {
	content?: string;
	className?: string;
}

export const ArticleText: React.FC<ArticleTextProps> = ({ className, content }) => {
	const ref = useRef<HTMLDivElement>(null);
	const dictionary = useContext(DictionaryContext);

	// Deferred on the server too, so YouTube iframes never load before consent is checked
	const deferredContent = deferYouTubeIframes(content ?? '');
	const consentPlaceholderTargets = useYouTubeIframeConsent(ref, deferredContent);
	// React 19 resets innerHTML whenever this object changes, which would undo the hook's DOM changes
	const html = useMemo(() => ({ __html: deferredContent }), [deferredContent]);

	return (
		<Container width="Slim">
			<article className={className}>
				<div ref={ref} className={classNames(styles.ArticleText, className)} dangerouslySetInnerHTML={html}></div>
				{consentPlaceholderTargets.map(({ element, fillsContainer }, index) =>
					createPortal(
						<ConsentPlaceholder
							heading={dictionary.getValue('Video.ConsentHeading', null, 'Video unavailable')}
							text={dictionary.getValue(
								'Video.ConsentText',
								null,
								'To watch this video, please update your cookie preferences in the bottom-left corner of the page.',
							)}
							fillContainer={fillsContainer}
						/>,
						element,
						String(index),
					),
				)}
			</article>
		</Container>
	);
};
